import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import Item from "../components/Item";
import AdResourceService from "./AdResourceService";
import { fetchUrlAsBase64, readFileAsBase64 } from "./adReferenceImageUtils";
import {
    AD_GENERATE_RESOURCE_TYPES,
    getAdGenerateResourceType,
    SLIDING_NUMBER_MODE_PROMPTS,
    getAdResourcePreviewUrl,
    getAdResourceVideoUrl,
    isVideoAdResource,
} from "./adResourceTypes.config";
import {
    ADMIN_ADVERTISEMENT_PATH,
    advertisementResourceDetailPath,
} from "./advertisementRoutes";

const DEFAULT_RESOURCE_TYPE = AD_GENERATE_RESOURCE_TYPES[0]?.value ?? "";

export default function GenerateAdImageMain() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialResourceType =
        getAdGenerateResourceType(searchParams.get("resourceType") ?? "")?.value ??
        DEFAULT_RESOURCE_TYPE;

    const [resourceType, setResourceType] = useState(initialResourceType);
    const [storyId, setStoryId] = useState("");
    const [prompt, setPrompt] = useState("");
    const [referenceImageBase64, setReferenceImageBase64] = useState("");
    const [referenceImagePreview, setReferenceImagePreview] = useState(null);
    const [strength, setStrength] = useState("0.35");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [imageRefreshKey, setImageRefreshKey] = useState(0);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const typeConfig = useMemo(
        () => getAdGenerateResourceType(resourceType),
        [resourceType],
    );

    useEffect(() => {
        const config = getAdGenerateResourceType(initialResourceType);
        if (config?.storyIdRequired) {
            setStoryId(config.defaultStoryId ?? "");
        }
    }, [initialResourceType]);

    const handleResourceTypeChange = (nextType) => {
        const nextConfig = getAdGenerateResourceType(nextType);
        setResourceType(nextType);
        setReferenceImageBase64("");
        setReferenceImagePreview(null);
        setStrength("0.35");
        setStoryId(nextConfig?.storyIdRequired ? nextConfig.defaultStoryId ?? "" : "");
        setError(null);
        setResult(null);
    };

    const handleReferenceImageChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            setReferenceImageBase64("");
            setReferenceImagePreview(null);
            return;
        }
        try {
            const base64 = await readFileAsBase64(file);
            setReferenceImageBase64(base64);
            setReferenceImagePreview(URL.createObjectURL(file));
            if (error) setError(null);
        } catch (readError) {
            setReferenceImageBase64("");
            setReferenceImagePreview(null);
            setError(readError?.message || "Failed to read reference image");
        }
    };

    const handleClearReferenceImage = () => {
        setReferenceImageBase64("");
        setReferenceImagePreview(null);
    };

    const handleSubmit = useCallback(
        async (event) => {
            event?.preventDefault?.();

            const promptText = prompt.trim();
            const storyIdText = storyId.trim();

            if (typeConfig?.storyIdRequired && !storyIdText) {
                setError("Enter a story ID.");
                return;
            }
            if (typeConfig?.promptRequired !== false && !promptText) {
                setError("Enter a prompt.");
                return;
            }

            setError(null);
            setResult(null);
            setImageRefreshKey(0);
            setLoading(true);

            const response = await AdResourceService.generate({
                resourceType,
                prompt: promptText,
                storyId: storyIdText || undefined,
                referenceImage: referenceImageBase64 || undefined,
                strength: referenceImageBase64 ? strength : undefined,
            });
            setLoading(false);

            if (!response.success) {
                setError(response.error || "Request failed");
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: response.error || "Request failed",
                });
                return;
            }

            let data = response.data;
            if (data?.id) {
                const refreshed = await AdResourceService.getById(data.id);
                if (refreshed.success && refreshed.data) {
                    data = refreshed.data;
                }
            }

            setResult(data);
            setImageRefreshKey(Date.now());
            setSnackbar({
                open: true,
                severity: "success",
                message: isVideoAdResource(data) ? "Video generated" : "Image generated",
            });
        },
        [resourceType, prompt, storyId, referenceImageBase64, strength, typeConfig],
    );

    const resultPreviewUrl = result ? getAdResourcePreviewUrl(result) : null;
    const resultVideoUrl = result ? getAdResourceVideoUrl(result) : null;
    const resultIsVideo = isVideoAdResource(result);

    const displayPreviewUrl = resultPreviewUrl
        ? `${resultPreviewUrl}${resultPreviewUrl.includes("?") ? "&" : "?"}v=${imageRefreshKey}`
        : null;
    const displayVideoUrl = resultVideoUrl
        ? `${resultVideoUrl}${resultVideoUrl.includes("?") ? "&" : "?"}v=${imageRefreshKey}`
        : null;
    const displayMediaUrl = resultIsVideo ? displayVideoUrl : displayPreviewUrl;

    const previewWidth = result?.width ?? typeConfig?.width ?? 1024;
    const previewHeight = result?.height ?? typeConfig?.height ?? 1024;
    const isSquare = previewWidth === previewHeight;

    const canSubmit =
        resourceType &&
        (typeConfig?.storyIdRequired ? storyId.trim() : true) &&
        (typeConfig?.promptRequired !== false ? prompt.trim() : true);

    return (
        <Box sx={{ p: 3 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ADMIN_ADVERTISEMENT_PATH)}
                sx={{ mb: 2 }}
            >
                Back to resources
            </Button>

            <Item sx={{ textAlign: "left" }}>
                <Typography variant="h5" component="h1" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
                    Generate Media
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "left" }}>
                    POST /v1/ad/generate — Bedrock from <strong>resourceType</strong>
                    {typeConfig?.storyIdRequired ? (
                        <>
                            {" "}
                            + <strong>storyId</strong> (Luma Ray2 uses the story image)
                        </>
                    ) : (
                        <>
                            {" "}
                            + <strong>prompt</strong>. Optional <strong>referenceImage</strong> (base64) for
                            image-to-image.
                        </>
                    )}
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={2} sx={{ maxWidth: 720 }}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Resource type</InputLabel>
                            <Select
                                label="Resource type"
                                value={resourceType}
                                onChange={(e) => handleResourceTypeChange(e.target.value)}
                            >
                                {AD_GENERATE_RESOURCE_TYPES.map((entry) => (
                                    <MenuItem key={entry.value} value={entry.value}>
                                        {entry.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {typeConfig?.description ? (
                            <Typography variant="body2" color="text.secondary">
                                {typeConfig.description}
                            </Typography>
                        ) : null}

                        {typeConfig?.storyIdRequired ? (
                            <TextField
                                label="Story ID"
                                value={storyId}
                                onChange={(e) => {
                                    setStoryId(e.target.value);
                                    if (error) setError(null);
                                }}
                                required
                                fullWidth
                                size="small"
                                helperText="Story UUID — Luma Ray2 animates that story's image. Video is saved under story/{storyId}/video/ in S3."
                            />
                        ) : null}

                        <TextField
                            label={typeConfig?.promptRequired === false ? "Prompt (optional)" : "Prompt"}
                            value={prompt}
                            onChange={(e) => {
                                setPrompt(e.target.value);
                                if (error) setError(null);
                            }}
                            placeholder={typeConfig?.promptPlaceholder ?? ""}
                            required={typeConfig?.promptRequired !== false}
                            fullWidth
                            size="small"
                            multiline
                            minRows={7}
                        />

                        {typeConfig?.mediaKind !== "video" ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {SLIDING_NUMBER_MODE_PROMPTS.map((preset) => (
                                    <Button
                                        key={preset.id}
                                        type="button"
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setPrompt(preset.prompt);
                                            if (error) setError(null);
                                        }}
                                        sx={{ textTransform: "none" }}
                                    >
                                        {preset.label}
                                    </Button>
                                ))}
                            </Stack>
                        ) : null}

                        {typeConfig?.supportsReferenceImage !== false ? (
                        <Box>
                            <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none" }}>
                                Upload reference image
                                <input
                                    type="file"
                                    hidden
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={handleReferenceImageChange}
                                />
                            </Button>
                            {referenceImagePreview ? (
                                <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mt: 1.5 }}>
                                    <Box
                                        component="img"
                                        src={referenceImagePreview}
                                        alt="Reference"
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            objectFit: "contain",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            bgcolor: "action.hover",
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={handleClearReferenceImage}
                                        sx={{ textTransform: "none" }}
                                    >
                                        Remove
                                    </Button>
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                    Optional PNG, JPEG, or WebP. Uses Bedrock image-to-image when set.
                                </Typography>
                            )}
                        </Box>
                        ) : null}

                        {typeConfig?.supportsReferenceImage !== false && referenceImageBase64 ? (
                            <TextField
                                label="Strength"
                                type="number"
                                value={strength}
                                onChange={(e) => setStrength(e.target.value)}
                                helperText="0 = close to reference, 1 = more prompt influence. Default 0.35."
                                fullWidth
                                size="small"
                                inputProps={{ min: 0, max: 1, step: 0.05 }}
                            />
                        ) : null}

                        <Box>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading || !canSubmit}
                                sx={{ textTransform: "none" }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                                        Generating…
                                    </>
                                ) : (
                                    "Generate"
                                )}
                            </Button>
                        </Box>

                        {error ? (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        ) : null}
                    </Stack>
                </Box>
            </Item>

            {result ? (
                <Card sx={{ mt: 3, maxWidth: 1100 }}>
                    <CardContent>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
                        >
                            <Typography variant="h6">Result</Typography>
                            {result.id ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => navigate(advertisementResourceDetailPath(result.id))}
                                    sx={{ textTransform: "none" }}
                                >
                                    View resource
                                </Button>
                            ) : null}
                        </Stack>
                        <Box
                            component="pre"
                            sx={{
                                m: 0,
                                mb: 2,
                                p: 2,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                                overflow: "auto",
                                fontFamily: "monospace",
                                fontSize: "0.8125rem",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            {JSON.stringify(result, null, 2)}
                        </Box>

                        {displayMediaUrl ? (
                            <>
                                {resultIsVideo ? (
                                    <Box
                                        component="video"
                                        key={imageRefreshKey}
                                        src={displayVideoUrl ?? undefined}
                                        poster={displayPreviewUrl ?? undefined}
                                        controls
                                        playsInline
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            maxWidth: 720,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            bgcolor: "grey.900",
                                            mb: 1.5,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        key={imageRefreshKey}
                                        component="img"
                                        src={displayMediaUrl}
                                        alt={result.resourceType ?? "Generated ad image"}
                                        referrerPolicy="no-referrer"
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            maxWidth: previewWidth,
                                            height: "auto",
                                            aspectRatio: isSquare ? "1 / 1" : `${previewWidth} / ${previewHeight}`,
                                            objectFit: "contain",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: isSquare ? 2 : 1,
                                            bgcolor: "action.hover",
                                            mb: 1.5,
                                        }}
                                    />
                                )}
                                {!resultIsVideo ? (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 1 }}
                                    >
                                        {previewWidth} × {previewHeight}
                                    </Typography>
                                ) : null}
                                <Button
                                    component="a"
                                    href={resultIsVideo ? resultVideoUrl : resultPreviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    sx={{ textTransform: "none" }}
                                >
                                    {resultIsVideo ? "Open video" : "Open image"}
                                </Button>
                            </>
                        ) : null}
                    </CardContent>
                </Card>
            ) : null}

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(v) => setSnackbar((s) => ({ ...s, open: v }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
